/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dialog } from 'primereact/dialog';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { RefreshButton } from 'sharedUi/RefreshButton';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import SourceViewDialog from './source-view-dialog';
import FormSelect from '../../components/forms/form-select';
import { pipelinesService } from '../../services/pipelines.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { PipelineSource } from '../../types';
import { formatDate } from 'sharedUi/dateUtils';
import { StatusChip } from '../../components/common/panel';
import { PANEL, PRETTY } from '../../components/common/ui-kit';
import { sourceTone } from './pipeline-status';

/** 40×40 keeps every row action above the desktop hit-area floor. */
const srcRowAction = 'ui-press flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700';

const fmtBytes = (n?: number | null) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

interface Props {
  pipelineId: string;
  supportedTypes: string[];
  canEdit: boolean;
}

/** Spec §12.2 "Sources" tab: attach FILE / TEXT / URL sources, see their status, detach. */
const PipelineSourcesPanel = ({ pipelineId, supportedTypes, canEdit }: Props) => {
  const toast = useRef<Toast>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<PipelineSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [uploading, setUploading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addKind, setAddKind] = useState<'TEXT' | 'URL'>('TEXT');
  const [addTitle, setAddTitle] = useState('');
  const [addBody, setAddBody] = useState('');
  const [adding, setAdding] = useState(false);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [viewId, setViewId] = useState<string | null>(null);

  const showError = useCallback((m: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: m, life: 10000 }), []);
  const showOk = useCallback((m: string) => toast.current?.show({ severity: 'success', summary: 'Done', detail: m, life: 3000 }), []);

  const fetchRows = useCallback(async (page = 1, size = pageSize) => {
    try {
      setLoading(true);
      const res = await pipelinesService.listSources(pipelineId, { page, page_size: size });
      setRows(res.data || []);
      setTotal(res.pagination?.totalItems ?? res.pagination?.total ?? res.total ?? 0);
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to load sources'));
    } finally {
      setLoading(false);
    }
  }, [pipelineId, pageSize, showError]);

  useEffect(() => { fetchRows(1, pageSize); }, [pipelineId]); // eslint-disable-line react-hooks/exhaustive-deps

  const accept = supportedTypes.map((t) => ({ PDF: '.pdf', DOCX: '.docx', TXT: '.txt', MD: '.md', HTML: '.html,.htm', CSV: '.csv', JSON: '.json' }[t] || '')).filter(Boolean).join(',');

  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setUploading(true);
      await pipelinesService.uploadSource(pipelineId, file);
      showOk(`${file.name} added`);
      fetchRows(1, pageSize);
    } catch (err) {
      showError(extractErrorMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const onAdd = async () => {
    try {
      setAdding(true);
      await pipelinesService.addSource(pipelineId, addKind === 'TEXT'
        ? { sourceType: 'TEXT', text: addBody, title: addTitle || undefined }
        : { sourceType: 'URL', url: addBody.trim(), title: addTitle || undefined });
      showOk(`${addKind} source added`);
      setAddOpen(false); setAddTitle(''); setAddBody('');
      fetchRows(1, pageSize);
    } catch (err) {
      showError(extractErrorMessage(err, 'Could not add the source'));
    } finally {
      setAdding(false);
    }
  };

  const onRemove = async () => {
    if (!removeId) return;
    try {
      setRemoving(true);
      await pipelinesService.removeSource(pipelineId, removeId);
      setRemoveId(null);
      fetchRows(Math.floor(first / pageSize) + 1, pageSize);
    } catch (err) {
      showError(extractErrorMessage(err, 'Could not remove the source'));
    } finally {
      setRemoving(false);
    }
  };

  const onValidate = async (id: string) => {
    try {
      await pipelinesService.validateSource(pipelineId, id);
      fetchRows(Math.floor(first / pageSize) + 1, pageSize);
    } catch (err) {
      showError(extractErrorMessage(err, 'Validation failed'));
    }
  };

  const columns: SaasColumn<PipelineSource>[] = [
    { field: 'fileName', header: 'Source', hideable: false, body: (r) => (
      <div className="min-w-0">
        <div className="text-sm text-ink truncate">{r.title || r.fileName || r.url || r._id}</div>
        {r.title && (r.fileName || r.url) && <div className="truncate font-mono text-xs text-ink-muted">{r.fileName || r.url}</div>}
      </div>
    ) },
    { field: 'sourceType', header: 'Kind', body: (r) => <StatusChip tone="info" dot={false}>{r.sourceType}</StatusChip> },
    { field: 'fileType', header: 'Type', body: (r) => r.fileType || <span className="text-ink-muted">—</span> },
    { field: 'fileSize', header: 'Size', align: 'right', body: (r) => <span className="tabular-nums">{fmtBytes(r.fileSize)}</span> },
    { field: 'processingStatus', header: 'Status', body: (r) => (
      <div className="flex items-center gap-1.5">
        <StatusChip tone={sourceTone(r.processingStatus)}>{r.processingStatus}</StatusChip>
        {r.validationError && <span className="ui-ink ui-ink--bad max-w-[220px] truncate text-xs" title={r.validationError}>{r.validationError}</span>}
      </div>
    ) },
    { field: 'uploadedAt', header: 'Added', body: (r) => <span className="tabular-nums">{formatDate(r.uploadedAt)}</span> },
    // Viewing is a read action, so it stays out of the canEdit gate the write
    // actions sit behind — without it a row's metadata, images and indexed text
    // were unreachable from the UI at all.
    { field: 'actions', header: '', align: 'center', hideable: false, body: (r) => (
      <div className="flex justify-center gap-0.5">
        <button type="button" className={srcRowAction} onClick={() => setViewId(r._id)}
                data-pr-tooltip="View metadata" aria-label="View source metadata"
                data-testid="PipelineSources.View">
          <i className="pi pi-eye ui-act--view text-[1.05rem]" aria-hidden />
        </button>
        {/* Only this pipeline's own uploads: a knowledge-base document is shared
            by every pipeline reading the corpus, and is managed on the KB screen. */}
        {canEdit && String(r.pipelineRef) === String(pipelineId) && (
          <>
            <button type="button" className={srcRowAction} onClick={() => onValidate(r._id)} data-pr-tooltip="Re-validate" aria-label="Re-validate source">
              <i className="pi pi-check-square ui-act--edit text-[1.05rem]" aria-hidden />
            </button>
            <button type="button" className={srcRowAction} onClick={() => setRemoveId(r._id)} data-pr-tooltip="Remove" aria-label="Remove source">
              <i className="pi pi-trash ui-act--delete text-[1.05rem]" aria-hidden />
            </button>
          </>
        )}
      </div>
    ) },
  ];

  return (
    <div data-testid="Pipeline.Sources">
      <Toast ref={toast} position="top-right" />
      <input ref={fileInput} type="file" accept={accept} className="hidden" onChange={onFilePicked} />
      <div className={`${PANEL} overflow-hidden`}>
      <SaasDataTable
        fillHeight={false}
        tableId="pipeline-sources" columns={columns} data={rows} loading={loading} dataKey="_id"
        emptyMessage="No sources yet — upload a file or add text / a URL." first={first} rows={pageSize} totalRecords={total}
        onPage={(e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(Math.floor(e.first / e.rows) + 1, e.rows); }}
        rowsPerPageOptions={[10, 25, 50]} onSort={() => {}} onFilter={() => {}} testId="PipelineSources"
        toolbarEnd={
          <>
            <span className="whitespace-nowrap text-sm tabular-nums text-ink-muted">{total} {total === 1 ? 'source' : 'sources'}</span>
            <RefreshButton onClick={() => fetchRows(Math.floor(first / pageSize) + 1, pageSize)} loading={loading} />
            {canEdit && (
              <>
                <span className="ui-press inline-flex">
                  <Button type="button" label="Add text / URL" icon="pi pi-plus" size="small" severity="secondary" outlined onClick={() => setAddOpen(true)} />
                </span>
                <span className="ui-press inline-flex">
                  <Button type="button" label={uploading ? 'Uploading…' : 'Upload file'} icon={uploading ? 'pi pi-spin pi-spinner' : 'pi pi-upload'} size="small" disabled={uploading || !accept}
                          onClick={() => fileInput.current?.click()} data-testid="PipelineSources.Upload" />
                </span>
              </>
            )}
          </>
        }
      />
      </div>
      <p className="mt-2 text-xs leading-relaxed text-ink-muted" style={PRETTY}>
        Accepted: {supportedTypes.join(', ') || '—'}. Max 50 MB per file. Identical files are rejected by checksum.
      </p>

      <Dialog header="Add a source" visible={addOpen} onHide={() => setAddOpen(false)} style={{ width: '520px' }} modal>
        <div className="flex flex-col gap-3">
          <FormSelect label="Kind" name="kind" value={addKind} options={[{ label: 'Pasted text', value: 'TEXT' }, { label: 'URL', value: 'URL' }]}
                      onChange={(e: any) => setAddKind((e.target ? e.target.value : e) as 'TEXT' | 'URL')} />
          <Input label="Title" value={addTitle} onChange={(v: string) => setAddTitle(v)} placeholder="Optional" />
          {addKind === 'TEXT'
            ? <TextArea label="Text" value={addBody} onChange={(v: string) => setAddBody(v)} rows={8} placeholder="Paste the content to index" />
            : <Input label="URL" value={addBody} onChange={(v: string) => setAddBody(v)} placeholder="https://…" />}
          <div className="flex justify-end gap-2">
            <span className="ui-press inline-flex"><Button type="button" label="Cancel" severity="secondary" text onClick={() => setAddOpen(false)} /></span>
            <span className="ui-press inline-flex">
              <Button type="button" label={adding ? 'Adding…' : 'Add'} icon={adding ? 'pi pi-spin pi-spinner' : 'pi pi-plus'} disabled={adding || !addBody.trim()} onClick={onAdd} />
            </span>
          </div>
        </div>
      </Dialog>

      <SourceViewDialog pipelineId={pipelineId} sourceId={viewId} onHide={() => setViewId(null)} />

      <DeleteConfirmDialog visible={!!removeId} onCancel={() => setRemoveId(null)} onDelete={onRemove}
                           message="Remove this source? Artifacts already built from it are unaffected; the next run will not include it."
                           testId="PipelineSources.Dialog.Remove" loading={removing}
                           header="Confirm removal" confirmLabel="Remove" loadingLabel="Removing..." />
    </div>
  );
};

export default PipelineSourcesPanel;
